//Angular Core
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import {
  BrowserModule,
  HammerGestureConfig,
  HAMMER_GESTURE_CONFIG,
} from "@angular/platform-browser";
import { NgModule } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";

import { HttpClient, HttpClientModule } from "@angular/common/http";
import { RouterModule } from "@angular/router";

//Routing
import { AppRoutes } from "./app.routing";
import { AppComponent } from "./app.component";

//Layouts
import { BlankComponent, RootLayout, ExecutiveLayout } from "./@pages/layouts";
//Layout Service - Required
import { pagesToggleService } from "./@pages/services/toggler.service";

//Shared Layout Components

import { HeaderComponent } from "./@pages/components/header/header.component";
import { HorizontalMenuComponent } from "./@pages/components/horizontal-menu/horizontal-menu.component";
import { SharedModule } from "./@pages/components/shared.module";
import { pgListViewModule } from "./@pages/components/list-view/list-view.module";
import { pgCardModule } from "./@pages/components/card/card.module";
import { pgCardSocialModule } from "./@pages/components/card-social/card-social.module";

//Basic Bootstrap Modules
import {
  BsDropdownModule,
  AccordionModule,
  AlertModule,
  ButtonsModule,
  CollapseModule,
  ModalModule,
  ProgressbarModule,
  TabsModule,
} from "ngx-bootstrap";

//Pages Globaly required Components - Optional
import { pgTabsModule } from "./@pages/components/tabs/tabs.module";
import { pgSwitchModule } from "./@pages/components/switch/switch.module";
import { ProgressModule } from "./@pages/components/progress/progress.module";

//Thirdparty Components / Plugins - Optional
import { NvD3Module } from "ngx-nvd3";
import { NgxEchartsModule } from "ngx-echarts";
import { IsotopeModule } from "ngx-isotope";

import { NgxDnDModule } from "@swimlane/ngx-dnd";
import { QuillModule } from "ngx-quill";
import { PerfectScrollbarModule } from "ngx-perfect-scrollbar";
import { PERFECT_SCROLLBAR_CONFIG } from "ngx-perfect-scrollbar";
import { PerfectScrollbarConfigInterface } from "ngx-perfect-scrollbar";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { DialogsService } from "./dialogs/dialogs.service";
import { WebsocketService } from "./socket.io/websocket.service";
import { AuthService } from "./auth/auth.service";
import { TagVerificationDialogComponent } from "./dialogs/tag-verification-dialog/tag-verification-dialog.component";
import { ErrorDialogComponent } from "./dialogs/error-dialog/error-dialog.component";
import { MessageService } from "./@pages/components/message/message.service";
import { MessageModule } from "./@pages/components/message/message.module";
import { ChangePasswordDialogComponent } from "./dialogs/change-password/change-password-dialog.component";
import { environment } from "../environments/environment";
import { RECAPTCHA_SETTINGS, RecaptchaSettings } from "ng-recaptcha";
import { SpinnerComponent } from "./spinner.component";
import { AuthGuard } from "./auth/auth.guard";
import { CookieLawModule } from "angular2-cookie-law";
import { TermsDialogComponent } from "./dialogs/terms/terms-dialog.component";

import { UserIdleModule } from "angular-user-idle";

export function createTranslateLoader(http: HttpClient) {
  // return new TranslateHttpLoader(http, './assets/i18n/', '.json');
  return new TranslateHttpLoader(http);
}

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true,
};

//Hammer Config Overide
//https://github.com/angular/angular/issues/10541
export class AppHammerConfig extends HammerGestureConfig {
  overrides = <any>{
    pinch: { enable: false },
    rotate: { enable: false },
  };
}

@NgModule({
  declarations: [
    AppComponent,
    SpinnerComponent,
    BlankComponent,
    RootLayout,
    ExecutiveLayout,
    HeaderComponent,
    HorizontalMenuComponent,
    ErrorDialogComponent,
    TermsDialogComponent,
    TagVerificationDialogComponent,
    ChangePasswordDialogComponent,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    FormsModule,
    HttpClientModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    SharedModule,
    ProgressModule,
    pgListViewModule,
    pgCardModule,
    pgCardSocialModule,
    RouterModule.forRoot(AppRoutes),
    BsDropdownModule.forRoot(),
    AccordionModule.forRoot(),
    AlertModule.forRoot(),
    ButtonsModule.forRoot(),
    CollapseModule.forRoot(),
    ModalModule.forRoot(),
    ProgressbarModule.forRoot(),
    TabsModule.forRoot(),

    NvD3Module,
    pgTabsModule,
    // NgxEchartsModule, 2018-10-05
    IsotopeModule,
    NgxDnDModule,
    QuillModule,
    PerfectScrollbarModule,
    pgSwitchModule,
    MessageModule,
    CookieLawModule,
    UserIdleModule.forRoot({ idle: 3600, timeout: 15, ping: 120 }),
  ],
  providers: [
    AuthService,
    pagesToggleService,
    DialogsService,
    WebsocketService,
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
    },
    { provide: HAMMER_GESTURE_CONFIG, useClass: AppHammerConfig },
    MessageService,
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: { siteKey: environment.googleRecaptcha } as RecaptchaSettings,
    },
    AuthGuard,
  ],
  bootstrap: [AppComponent],
  entryComponents: [
    TermsDialogComponent,
    ErrorDialogComponent,
    TagVerificationDialogComponent,
    ChangePasswordDialogComponent,
  ],
})
export class AppModule {}
